import { useMutation, useQueryClient } from '@tanstack/react-query';
import LangEnum from '../../../lang/LangEnum';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../modules/context/loadingStatus/LoadingStatus.store';
import {
  validateOrderConditionsQuery,
  validateOrderGeneralDataQuery,
  validateOrderProductsQuery,
} from '../../../modules/orders/contexts/order.utils';
import { ordersRepository } from '../../../modules/orders/repositories/ordersRepository/orders.repository';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import useOrderProductsReloadStore from '../../../stores/OrderProductsReload/orderProductsReload.store';
import snackbarStore from '../../../stores/Snackbar.store';
import {
  ORDER_ACTIVITY_FEED_KEY_ROOT,
  matchActivityFeedQuery,
} from '../activityFeed/activityFeed.keys';
import { getFindOrderConditionsKey } from '../orderCondition/orderCondition.keys';
import { getFindProductGridRootKey } from '../product-grid/product-grid.keys';
import { getOrderQueryKey } from './order.keys';
import {
  ForceSyncOrderParams,
  UpdateOrderGeneralDataParams,
  UseForceSyncOrderMutationParams,
  UseUpdateOrderGeneralDataMutationParams,
} from './order.types';

const parseErrorCode = (error: Error): LangEnum => {
  try {
    return JSON.parse(error.message).code ?? LangEnum.GLOBAL_ERROR_MESSAGE;
  } catch {
    return LangEnum.GLOBAL_ERROR_MESSAGE;
  }
};

const ORDER_PRODUCTS_AFFECTING_FIELDS: (keyof UpdateOrderGeneralDataParams)[] = [
  'customerId',
  'priceTableId',
  'salesRepresentativeId',
];

const hasOrderProductsAffectingChange = (data: UpdateOrderGeneralDataParams) =>
  ORDER_PRODUCTS_AFFECTING_FIELDS.some((field) => data[field] !== undefined);

export const useUpdateOrderGeneralDataMutation = ({
  orderId,
  currentCustomerId,
  isOfflineMode,
  onSuccess,
  onPriceTableConflict,
  onOrderArchived,
}: UseUpdateOrderGeneralDataMutationParams) => {
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);

  const orderQueryKey = [team?.id, ...getOrderQueryKey({ orderId })];

  const { isPending, mutate: updateOrderGeneralData } = useMutation({
    networkMode: isOfflineMode ? 'always' : undefined,
    mutationFn: async (data: UpdateOrderGeneralDataParams) =>
      handleLoadingStatus({
        requestFn: () => {
          const hasCustomerChanged =
            data.customerId !== undefined &&
            (data.customerId ?? null) !== (currentCustomerId ?? null);

          const repository = ordersRepository(orderId, isOfflineMode);

          if (hasCustomerChanged) {
            return repository.updateOrderCustomer(
              data.customerId || null,
              data.allowUpdatePriceTable,
            );
          }

          return repository.update({ id: orderId, ...data });
        },
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderQueryKey });
    },
    onSuccess: async (_response, data) => {
      const hasOrderProductsChanged = hasOrderProductsAffectingChange(data);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orderQueryKey }),
        queryClient.resetQueries({
          queryKey: [team?.id, ...getFindOrderConditionsKey({ orderId })],
        }),
        queryClient.resetQueries({ queryKey: [team?.id, ...getFindProductGridRootKey()] }),
      ]);

      await Promise.all([
        queryClient.refetchQueries(validateOrderGeneralDataQuery(orderId, team?.id)),
        queryClient.refetchQueries(validateOrderConditionsQuery(orderId, team?.id)),
        hasOrderProductsChanged &&
          queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id)),
      ]);

      if (hasOrderProductsChanged) {
        useOrderProductsReloadStore.getState().requestReload(orderId);
      }

      await onSuccess?.();
    },
    onError: (error: Error) => {
      const code = parseErrorCode(error);

      if (code === LangEnum.API_EXCEPTION_ORDER_UNPROCESSABLE_RULES_PRICE_TABLE_TO_UPDATE) {
        onPriceTableConflict?.();
        return;
      }

      if (code === LangEnum.API_EXCEPTION_ORDER_ARCHIVED) {
        onOrderArchived?.();
      }

      handleMessage({ message: translate(code), type: 'error' });
    },
  });

  return { isPending, updateOrderGeneralData };
};

export const useForceSyncOrderMutation = ({ onSettled }: UseForceSyncOrderMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const { isPending, mutate: forceSyncOrder } = useMutation({
    mutationFn: async ({ orderId }: ForceSyncOrderParams) => {
      const response = await ordersRepository(orderId).forceOrderSync();

      if (!response.ok) {
        throw new Error(
          response.data?.message ?? translate(LangEnum.SYNC_HISTORY_FORCE_SYNC_ERROR_MESSAGE),
        );
      }
    },
    onSuccess: async (_data, { orderId }: ForceSyncOrderParams) => {
      handleMessage({ type: 'info', message: translate(LangEnum.SNACKBAR_REQUEST_SENT_FOR_SYNC) });

      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getOrderQueryKey({ orderId })],
      });

      await queryClient.invalidateQueries({
        predicate: matchActivityFeedQuery({
          keyRoot: ORDER_ACTIVITY_FEED_KEY_ROOT,
          entityId: orderId,
          teamId: team?.id,
        }),
      });
    },
    onError: (error: Error) => {
      handleMessage({ type: 'error', message: error.message });
    },
    onSettled,
  });

  return { isPending, forceSyncOrder };
};

export const useForceReverseSyncOrderMutation = ({
  onSettled,
}: UseForceSyncOrderMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const { isPending, mutate: forceReverseSyncOrder } = useMutation({
    mutationFn: async ({ orderId }: ForceSyncOrderParams) => {
      const response = await ordersRepository(orderId).forceReverseOrderSync();

      if (!response.ok) {
        throw new Error(
          response.data?.message ??
            translate(LangEnum.SYNC_HISTORY_FORCE_REVERSE_SYNC_ERROR_MESSAGE),
        );
      }
    },
    onSuccess: async (_data, { orderId }: ForceSyncOrderParams) => {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.SNACKBAR_REQUEST_SENT_FOR_REVERSE_SYNC),
      });

      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getOrderQueryKey({ orderId })],
      });

      await queryClient.invalidateQueries({
        predicate: matchActivityFeedQuery({
          keyRoot: ORDER_ACTIVITY_FEED_KEY_ROOT,
          entityId: orderId,
          teamId: team?.id,
        }),
      });
    },
    onError: (error: Error) => {
      handleMessage({ type: 'error', message: error.message });
    },
    onSettled,
  });

  return { isPending, forceReverseSyncOrder };
};
